import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Users, BookOpen, TrendingUp, Award, Settings, Search,
  Home, BarChart2, Clock, RefreshCcw, User, X,
  ChevronRight, Plane, GraduationCap, Target, Trophy,
  Eye, FileText, ChevronDown, ChevronUp, Flame, Star,
  Globe, Mic, Layers, Calendar, Bell, LogOut, Menu,
  PieChart, Activity, UserPlus, CheckCircle, Book,
  MessageSquare, HelpCircle, Shield, Database, Zap,
  Phone, Wallet, Edit, Mail, Trash2, FileSpreadsheet, Upload, CheckCircle2, AlertCircle, Send,
  ChevronLeft, BarChart3, Headphones, AlertTriangle, Video, Palette, CreditCard, Receipt,
  Download, ExternalLink, Briefcase
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import {
  StudentManagement,
  ContentManagement,
  LevelTestManagement,
  ScoringManagement,
  SkillTestManagement,
  EducationProgramsManagement,
  BulkCertificatesManagement,
  SmsReminderManagement,
  EmailReminderManagement
} from '../components/AdminComponents';

// Modüler Admin Bileşenleri
import { AdminOnline } from '../components/admin/AdminOnline';
import { PodcastManagement } from '../components/admin/PodcastManagement';
import StudentProfileViewer from '../components/admin/StudentProfileViewer';
import { AdminMessages } from '../components/admin/AdminMessages';
import AgreementEditor from '../components/admin/AgreementEditor';
import PaymentManagement from '../components/admin/PaymentManagement';
import { 
  SpinWheelManagement, 
  CareerLevelsManagement, 
  DailyTasksManagement, 
  FlightTicketsManagement,
  GamificationStatsContent
} from '../components/admin/AdminGamification';
import { VideoCoursesManagement } from '../components/admin/VideoCoursesManagement';
import { CertificateManagement } from '../components/admin/CertificateManagement';
import CertificateDesigner from '../components/admin/CertificateDesigner';

// Modüler Dashboard Component'leri
import { 
  StudentsContent, 
  LeaderboardContent,
  DashboardContent,
  StudentHubContent,
  ReportsHubContent,
  EgitimlerHubContent,
  SertifikalarHubContent,
  MuhasebeHubContent,
  RegistrationRequestsContent,
  StudentReportsContent,
  StudentSearchContent,
  ReportsContent,
  CounselorsContent,
  StatisticsContent,
  JobsManagement,
  VideoInterviewManagement,
  InterviewHubManagement,
  PsychologyTestsManagement
} from '../components/admin/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [openMenu, setOpenMenu] = useState(null); // Tek bir açık menü: 'egitimler', 'certificates', 'interactive', veya null
  const [students, setStudents] = useState([]);
  const [studentFilter, setStudentFilter] = useState(''); // '', 'never_logged_in', 'no_agreements', 'unpaid'
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Menü açma fonksiyonu - diğerlerini kapatır
  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dinamik grafik verileri
  const [weeklyActivityData, setWeeklyActivityData] = useState([]);
  const [levelDistributionData, setLevelDistributionData] = useState([]);
  const [placementResults, setPlacementResults] = useState([]);
  const [placementPage, setPlacementPage] = useState(1);
  const [placementTotal, setPlacementTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [counselors, setCounselors] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSendingId, setEmailSendingId] = useState(null);
  const [smsSendingId, setSmsSendingId] = useState(null);
  
  // Kayıt talepleri state
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  
  // Modal states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [profileViewStudentId, setProfileViewStudentId] = useState(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showCounselorModal, setShowCounselorModal] = useState(false);
  const [editingCounselor, setEditingCounselor] = useState(null);
  const [newCounselor, setNewCounselor] = useState({ name: '', email: '', phone: '', title: '', specialization: '', is_active: true });
  const [newStudent, setNewStudent] = useState({ 
    name: '', 
    email: '', 
    password: '',
    tc_no: '',
    birth_date: '',
    phone: '',
    education_level: '',
    gender: '',
    city: '',
    address: '',
    profile_photo: '',
    assigned_courses: [],
    total_fee: 0,
    paid_amount: 0,
    counselor_id: ''
  });
  const [addingStudent, setAddingStudent] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Excel Import states
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Password change states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'instructor') {
      toast.error('Bu sayfaya erişim yetkiniz yok');
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Filtre değiştiğinde yeniden yükle
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'instructor') {
      loadData(studentFilter);
    }
  }, [studentFilter]);

  const loadData = async (filter = studentFilter) => {
    try {
      setLoading(true);
      const filterParam = filter ? `?filter_type=${filter}` : '';
      const [studentsRes, statsRes, counselorsRes, unreadRes, requestsRes, weeklyRes, levelRes, placementRes] = await Promise.all([
        axios.get(`${API}/admin/students${filterParam}`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/counselors`, { withCredentials: true }),
        axios.get(`${API}/admin/advisor/unread-count`, { withCredentials: true }).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/admin/registration-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/weekly-activity`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/level-distribution`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/placement-results`, { withCredentials: true }).catch(() => ({ data: { results: [], total: 0 } }))
      ]);
      setStudents(studentsRes.data.students || []);
      setStats(statsRes.data);
      setCounselors(counselorsRes.data.counselors || []);
      setUnreadMessageCount(unreadRes.data?.unread_count || 0);
      setRegistrationRequests(requestsRes.data || []);
      setWeeklyActivityData(weeklyRes.data || []);
      setLevelDistributionData(levelRes.data || []);
      setPlacementResults(placementRes.data?.results || []);
      setPlacementTotal(placementRes.data?.total || 0);
    } catch (error) {
      console.error('Data loading error:', error);
      toast.error('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kayıt talebini onayla
  const approveRegistration = async (requestId) => {
    try {
      const res = await axios.post(`${API}/admin/registration-requests/${requestId}/approve`, {}, { withCredentials: true });
      if (res.data.user_id) {
        toast.success('Kayıt talebi onaylandı! Öğrenci oluşturuldu.');
        loadData();
      }
    } catch (error) {
      toast.error('Onaylama hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Kayıt talebini reddet
  const rejectRegistration = async (requestId) => {
    try {
      await axios.post(`${API}/admin/registration-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success('Kayıt talebi reddedildi');
      loadData();
    } catch (error) {
      toast.error('Reddetme hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const syncLeaderboardData = async () => {
    try {
      toast.loading('Veriler senkronize ediliyor...');
      const res = await axios.post(`${API}/admin/sync-leaderboard-data`, {}, { withCredentials: true });
      toast.dismiss();
      if (res.data.success) {
        toast.success(`✅ ${res.data.migrated_records} kayıt senkronize edildi! Toplam: ${res.data.total_cabin_lesson_results}`);
      } else {
        toast.error('Senkronizasyon başarısız');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Senkronizasyon hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadStudentDetail = async (studentId) => {
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API}/admin/student/${studentId}`, { withCredentials: true });
      setStudentDetail(res.data);
    } catch (error) {
      toast.error('Öğrenci detayları yüklenemedi');
    } finally {
      setDetailLoading(false);
    }
  };

  const openStudentModal = async (student) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
    await loadStudentDetail(student.id);
  };

  const handleAddStudent = async () => {
    if (!newStudent.name || !newStudent.email || !newStudent.password) {
      toast.error('Ad, e-posta ve şifre zorunludur');
      return;
    }
    
    if (newStudent.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    try {
      setAddingStudent(true);
      await axios.post(`${API}/admin/add-student`, newStudent, { withCredentials: true });
      
      toast.success('Öğrenci başarıyla eklendi!');
      setShowAddStudentModal(false);
      setNewStudent({ 
        name: '', email: '', password: '', tc_no: '', birth_date: '', phone: '',
        education_level: '', gender: '', city: '', address: '', profile_photo: '',
        assigned_courses: [], total_fee: 0, paid_amount: 0
      });
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Öğrenci eklenirken bir hata oluştu';
      toast.error(errorMessage);
    } finally {
      setAddingStudent(false);
    }
  };

  const handleUpdateStudent = async () => {
    if (!selectedStudent) return;
    
    try {
      setAddingStudent(true);
      await axios.put(`${API}/admin/student/${selectedStudent.id}`, newStudent, { withCredentials: true });
      
      toast.success('Öğrenci bilgileri güncellendi!');
      setShowAddStudentModal(false);
      setEditMode(false);
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Güncelleme sırasında bir hata oluştu';
      toast.error(errorMessage);
    } finally {
      setAddingStudent(false);
    }
  };

  // Excel Import fonksiyonu
  const handleExcelImport = async () => {
    if (!excelFile) {
      toast.error('Lütfen bir Excel dosyası seçin');
      return;
    }
    
    try {
      setImporting(true);
      setImportResult(null);
      
      const formData = new FormData();
      formData.append('file', excelFile);
      
      const response = await axios.post(`${API}/admin/import-students-excel`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      toast.success(`${response.data.imported} öğrenci başarıyla aktarıldı!`);
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Excel dosyası işlenirken bir hata oluştu';
      toast.error(errorMessage);
      setImportResult({ error: errorMessage });
    } finally {
      setImporting(false);
    }
  };

  // Şifre değiştirme fonksiyonu
  const handleChangePassword = async () => {
    if (!selectedStudent) {
      toast.error('Öğrenci seçilmedi');
      return;
    }
    
    if (!newPassword || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    try {
      setChangingPassword(true);
      await axios.post(
        `${API}/admin/student/${selectedStudent.id}/change-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      
      toast.success('Şifre başarıyla değiştirildi!');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Şifre değiştirme sırasında bir hata oluştu';
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const openEditStudent = (student) => {
    setSelectedStudent(student);
    setNewStudent({
      name: student.name || '',
      email: student.email || '',
      password: '',
      tc_no: student.tc_no || '',
      birth_date: student.birth_date || '',
      phone: student.phone || '',
      education_level: student.education_level || '',
      gender: student.gender || '',
      city: student.city || '',
      address: student.address || '',
      profile_photo: student.profile_photo || '',
      assigned_courses: student.assigned_courses || [],
      total_fee: student.total_fee || 0,
      paid_amount: student.paid_amount || 0,
      counselor_id: student.counselor_id || ''
    });
    setEditMode(true);
    setShowAddStudentModal(true);
  };

  const toggleCourse = (course) => {
    const courses = newStudent.assigned_courses || [];
    if (courses.includes(course)) {
      setNewStudent({ ...newStudent, assigned_courses: courses.filter(c => c !== course) });
    } else {
      setNewStudent({ ...newStudent, assigned_courses: [...courses, course] });
    }
  };

  // Danışman fonksiyonları
  const handleAddCounselor = async () => {
    if (!newCounselor.name) {
      toast.error('Danışman adı zorunludur');
      return;
    }
    
    try {
      if (editingCounselor) {
        await axios.put(`${API}/admin/counselors/${editingCounselor.id}`, newCounselor, { withCredentials: true });
        toast.success('Danışman güncellendi');
      } else {
        await axios.post(`${API}/admin/counselors`, newCounselor, { withCredentials: true });
        toast.success('Danışman eklendi');
      }
      setShowCounselorModal(false);
      setNewCounselor({ name: '', email: '', phone: '', title: '', specialization: '', is_active: true });
      setEditingCounselor(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const handleDeleteCounselor = async (counselorId) => {
    if (!window.confirm('Bu danışmanı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/admin/counselors/${counselorId}`, { withCredentials: true });
      toast.success('Danışman silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  const openEditCounselor = (counselor) => {
    setEditingCounselor(counselor);
    setNewCounselor({
      name: counselor.name || '',
      email: counselor.email || '',
      phone: counselor.phone || '',
      title: counselor.title || '',
      specialization: counselor.specialization || '',
      is_active: counselor.is_active !== false
    });
    setShowCounselorModal(true);
  };

  const getCounselorName = (counselorId) => {
    const counselor = counselors.find(c => c.id === counselorId);
    return counselor ? counselor.name : '-';
  };

  // Öğrenci silme fonksiyonları
  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Bu öğrenciyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/student/${studentId}`, { withCredentials: true });
      toast.success('Öğrenci silindi');
      setStudents(students.filter(s => s.id !== studentId));
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteSelectedStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Lütfen silmek istediğiniz öğrencileri seçin');
      return;
    }
    
    if (!window.confirm(`${selectedStudents.length} öğrenciyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) return;
    
    setDeleteLoading(true);
    try {
      await axios.post(`${API}/admin/students/delete-bulk`, { student_ids: selectedStudents }, { withCredentials: true });
      toast.success(`${selectedStudents.length} öğrenci silindi`);
      setStudents(students.filter(s => !selectedStudents.includes(s.id)));
      setSelectedStudents([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toplu silme işlemi başarısız');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteAllStudents = async () => {
    if (!window.confirm('TÜM ÖĞRENCİLERİ silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) return;
    if (!window.confirm('Bu işlem TÜM öğrenci verilerini kalıcı olarak silecek. Devam etmek istediğinize emin misiniz?')) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/students/delete-all`, { withCredentials: true });
      toast.success('Tüm öğrenciler silindi');
      setStudents([]);
      setSelectedStudents([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toplu silme işlemi başarısız');
    } finally {
      setDeleteLoading(false);
    }
  };

  // SMS Gönderme Fonksiyonu
  const handleSendSMS = async (student) => {
    if (!student.phone) {
      toast.error('Bu öğrencinin telefon numarası yok');
      return;
    }
    
    if (!window.confirm(`${student.name} adlı öğrenciye giriş bilgilerini SMS olarak göndermek istiyor musunuz?`)) return;
    
    setSmsSendingId(student.id);
    setSmsLoading(true);
    
    try {
      // Giriş bilgileri mesajı
      const message = `Cabin Crew Training Giriş Bilgileriniz:\nE-posta: ${student.email}\nŞifre: ${student.plain_password || '(şifre gizli)'}\nGiriş: kabinmemurlugu.com`;
      
      const res = await axios.post(`${API}/admin/send-sms`, {
        student_id: student.id,
        phone: student.phone,
        message: message
      }, { withCredentials: true });
      
      if (res.data.success) {
        toast.success(`SMS gönderildi: ${student.name}`);
      } else {
        toast.error(res.data.message || 'SMS gönderilemedi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMS gönderme hatası');
    } finally {
      setSmsLoading(false);
      setSmsSendingId(null);
    }
  };


  // E-posta ile Giriş Bilgileri Gönder
  const handleSendCredentialsEmail = async (student) => {
    if (!student.email) {
      toast.error('Öğrencinin e-posta adresi yok!');
      return;
    }
    
    if (!window.confirm(`${student.name} adlı öğrenciye giriş bilgilerini E-POSTA olarak göndermek istiyor musunuz?`)) return;
    
    setEmailSendingId(student.id);
    setEmailLoading(true);
    
    try {
      const res = await axios.post(`${API}/api/admin/send-credentials-email`, {
        student_id: student.id,
        email: student.email,
        name: student.name,
        password: student.plain_password || '(şifre gizli)'
      }, { withCredentials: true });
      
      if (res.data.success) {
        toast.success(`E-posta gönderildi: ${student.name}`);
      } else {
        toast.error(res.data.message || 'E-posta gönderilemedi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-posta gönderme hatası');
    } finally {
      setEmailLoading(false);
      setEmailSendingId(null);
    }
  };

  // Toplu SMS Gönderme
  const handleSendBulkSMS = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Lütfen öğrenci seçin');
      return;
    }
    
    const studentsWithPhone = selectedStudents.filter(id => {
      const s = students.find(st => st.id === id);
      return s && s.phone;
    });
    
    if (studentsWithPhone.length === 0) {
      toast.error('Seçilen öğrencilerin hiçbirinin telefon numarası yok');
      return;
    }
    
    if (!window.confirm(`${studentsWithPhone.length} öğrenciye SMS göndermek istiyor musunuz?`)) return;
    
    setSmsLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/send-bulk-sms`, {
        student_ids: studentsWithPhone
      }, { withCredentials: true });
      
      toast.success(`${res.data.sent}/${res.data.total} SMS gönderildi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toplu SMS hatası');
    } finally {
      setSmsLoading(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Grafik verileri

  // Sol Menü
  const Sidebar = () => (
    <div className={cn(
      "fixed left-0 top-0 h-full bg-slate-900 text-white transition-all duration-300 z-20",
      sidebarOpen ? "w-64" : "w-20"
    )}>
      {/* Logo */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Plane className="w-5 h-5" />
          </div>
          {sidebarOpen && (
            <div>
              <h1 className="font-bold text-lg">AirAcademia</h1>
              <p className="text-xs text-slate-400">Admin Paneli</p>
            </div>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-180px)]">
        {/* Ana Sayfa */}
        <button
          onClick={() => setActiveSection('dashboard')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'dashboard' 
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Home className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Ana Sayfa</span>}
        </button>

        {/* Öğrenciler - Hub yapısı */}
        <button
          onClick={() => setActiveSection('student-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            (activeSection === 'student-hub' || activeSection.startsWith('student-')) 
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Users className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Öğrenci İşlemleri</span>}
          {sidebarOpen && (
            <span className="ml-auto bg-blue-500 text-xs px-2 py-0.5 rounded-full">
              {students.length}
            </span>
          )}
        </button>

        {/* Aktivite Logları */}
        <button
          onClick={() => navigate('/admin/activity-logs')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Activity className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Aktivite Logları</span>}
        </button>

        {/* Eğitimler - Tek buton, sayfa içinde sekmeler */}
        <button
          onClick={() => setActiveSection('egitimler-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            (activeSection === 'egitimler-hub' || activeSection.startsWith('egitim') || activeSection.startsWith('skill') || activeSection === 'video-courses' || activeSection.startsWith('gamification'))
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <BookOpen className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Eğitimler</span>}
        </button>

        {/* Sertifikalar - Tek buton, sayfa içinde sekmeler */}
        <button
          onClick={() => setActiveSection('sertifikalar-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            (activeSection === 'sertifikalar-hub' || activeSection === 'certificates' || activeSection === 'certificate-design' || activeSection === 'agreements' || activeSection === 'education-programs' || activeSection === 'bulk-certificates')
              ? "bg-amber-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Trophy className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Sertifikalar</span>}
        </button>

        {/* Muhasebe - Tek buton, sayfa içinde sekmeler */}
        <button
          onClick={() => setActiveSection('muhasebe-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            (activeSection === 'muhasebe-hub' || activeSection === 'accounting' || activeSection.startsWith('accounting-'))
              ? "bg-emerald-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Wallet className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Muhasebe</span>}
        </button>

        {/* Raporlar - Tek buton, sayfa içinde sekmeler */}
        <button
          onClick={() => setActiveSection('reports-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            (activeSection === 'reports-hub' || activeSection === 'statistics' || activeSection === 'reports' || activeSection === 'leaderboard' || activeSection === 'scoring' || activeSection === 'documents')
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <BarChart2 className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Raporlar</span>}
        </button>

        {/* Online İzleme */}
        <button
          onClick={() => setActiveSection('online')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'online' 
              ? "bg-green-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Activity className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Online</span>}
          {sidebarOpen && (
            <span className="ml-auto bg-green-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
              CANLI
            </span>
          )}
        </button>

        {/* Podcast Yönetimi */}
        <button
          onClick={() => setActiveSection("podcasts")}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === "podcasts" 
              ? "bg-purple-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Mic className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Podcastler</span>}
        </button>

        {/* Mesajlar & Bildirimler */}
        <button
          onClick={() => setActiveSection('messages')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative",
            activeSection === 'messages' 
              ? "bg-orange-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <MessageSquare className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Mesajlar & Bildirimler</span>}
          {/* Okunmamış mesaj badge */}
          {unreadMessageCount > 0 && (
            <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
            </span>
          )}
        </button>

        {/* İş İlanları */}
        <button
          onClick={() => setActiveSection('jobs-management')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'jobs-management' 
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Briefcase className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>İş İlanları</span>}
        </button>

        {/* Video Mülakat */}
        <button
          onClick={() => setActiveSection('video-interview')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'video-interview' 
              ? "bg-purple-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Video className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Video Mülakat</span>}
        </button>

        {/* Mülakat Soruları */}
        <button
          onClick={() => setActiveSection('interview-hub')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'interview-hub' 
              ? "bg-indigo-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Mülakat Soruları</span>}
        </button>

        {/* Psikoloji Testleri */}
        <button
          onClick={() => setActiveSection('psychology-tests')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'psychology-tests' 
              ? "bg-pink-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Shield className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Psikoloji Testleri</span>}
        </button>

        {/* Ayarlar */}
        <button
          onClick={() => setActiveSection('settings')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            activeSection === 'settings' 
              ? "bg-blue-600 text-white" 
              : "text-slate-300 hover:bg-slate-800"
          )}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Ayarlar</span>}
        </button>
      </nav>

      {/* User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-900">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold">{user?.name?.charAt(0) || 'A'}</span>
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          )}
          {sidebarOpen && (
            <button 
              onClick={() => { logout(); navigate('/'); }}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ===== YENİ ÖĞRENCİ KAYIT İÇERİĞİ =====
  const StudentRegisterContent = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Geri
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tek Öğrenci Ekleme */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Tek Öğrenci Ekle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 mb-4">Manuel olarak yeni öğrenci kaydı oluşturun</p>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowAddStudentModal(true)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Yeni Öğrenci Ekle
            </Button>
          </CardContent>
        </Card>

        {/* Excelden Toplu Ekleme */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Excelden Toplu Aktar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 mb-4">Excel dosyasından birden fazla öğrenci aktarın</p>
            <Button 
              variant="outline"
              className="w-full border-green-500 text-green-600 hover:bg-green-50"
              onClick={() => setShowExcelModal(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Excel Dosyası Yükle
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Son eklenen öğrenciler */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Son Eklenen Öğrenciler</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {students.slice(0, 10).map(student => (
              <div key={student.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-semibold">{student.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{student.name}</p>
                    <p className="text-xs text-gray-500">{student.email}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  {student.created_at ? new Date(student.created_at).toLocaleDateString('tr-TR') : '-'}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ===== ÖĞRENCİ MUHASEBE İÇERİĞİ =====
  const StudentAccountingContent = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Geri
        </Button>
      </div>

      {/* Ödeme Yönetimi Bileşenini Kullan */}
      <PaymentManagement students={students} />
    </div>
  );

  // Öğrenciler İçeriği - Modüler Component Kullanılıyor
  const StudentsContentWrapper = () => (
    <StudentsContent
      students={students}
      studentFilter={studentFilter}
      setStudentFilter={setStudentFilter}
      counselors={counselors}
      selectedStudents={selectedStudents}
      setSelectedStudents={setSelectedStudents}
      setActiveSection={setActiveSection}
      setShowExcelModal={setShowExcelModal}
      setShowAddStudentModal={setShowAddStudentModal}
      setSelectedStudent={setSelectedStudent}
      setShowStudentModal={setShowStudentModal}
      openEditStudent={openEditStudent}
      openStudentModal={openStudentModal}
      handleSendSMS={handleSendSMS}
      handleSendBulkSMS={handleSendBulkSMS}
      handleDeleteStudent={handleDeleteStudent}
      handleDeleteSelectedStudents={handleDeleteSelectedStudents}
      toggleStudentSelection={toggleStudentSelection}
      getCounselorName={getCounselorName}
      smsLoading={smsLoading}
      smsSendingId={smsSendingId}
      deleteLoading={deleteLoading}
    />
  );


  // Eğitim İçerikleri
  const TrainingContent = ({ type }) => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">
          {type === 'ingilizce' ? 'İngilizce Eğitimi' : type === 'kabin' ? 'Kabin Memurluğu Eğitimi' : 'Seviye Tespit Sınavı'}
        </h2>
        <p className="text-gray-500">
          {type === 'ingilizce' ? 'A0-C1 seviye müfredatı' : type === 'kabin' ? '16 modüllük kapsamlı eğitim' : '30 soruluk seviye belirleme'}
        </p>
      </div>

      {type === 'ingilizce' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { level: 'A0', title: 'Başlangıç Öncesi', units: 5, lessons: 25, color: 'from-gray-400 to-gray-500' },
            { level: 'A1', title: 'Başlangıç', units: 6, lessons: 35, color: 'from-green-400 to-green-500' },
            { level: 'A2', title: 'Temel', units: 6, lessons: 40, color: 'from-blue-400 to-blue-500' },
            { level: 'B1', title: 'Orta', units: 6, lessons: 45, color: 'from-purple-400 to-purple-500' },
            { level: 'B2', title: 'Orta-Üst', units: 6, lessons: 50, color: 'from-orange-400 to-orange-500' },
            { level: 'C1', title: 'İleri', units: 6, lessons: 55, color: 'from-red-400 to-red-500' },
          ].map(item => (
            <Card key={item.level} className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className={`h-2 bg-gradient-to-r ${item.color}`} />
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-3xl font-bold text-gray-800">{item.level}</span>
                  <GraduationCap className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-700 mb-2">{item.title}</h3>
                <div className="flex gap-4 text-sm text-gray-500">
                  <span>{item.units} ünite</span>
                  <span>{item.lessons} ders</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {type === 'kabin' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { id: 1, title: 'Kabin Memurluğuna Giriş', units: 6, icon: '✈️' },
            { id: 2, title: 'Havayolları Ne Bekler?', units: 6, icon: '🎯' },
            { id: 3, title: 'Aday Nasıl Davranmalı?', units: 6, icon: '🤝' },
            { id: 4, title: 'Kritik Hatalar', units: 6, icon: '⚠️' },
            { id: 5, title: 'Gerçek Mülakat Soruları', units: 7, icon: '❓' },
            { id: 6, title: 'Mülakat Psikolojisi', units: 7, icon: '🧠' },
            { id: 7, title: 'Open Day Hazırlık', units: 3, icon: '📅' },
            { id: 8, title: 'Grup Mülakatı', units: 7, icon: '👥' },
            { id: 9, title: 'Birebir Mülakat', units: 8, icon: '🎤' },
            { id: 10, title: 'Final Değerlendirme', units: 3, icon: '🎓' },
            { id: 11, title: 'Türk Havayolları', units: 5, icon: '🇹🇷' },
            { id: 12, title: 'Dünya Havayolları', units: 5, icon: '🌍' },
            { id: 13, title: 'Uçak Tipleri', units: 4, icon: '🛩️' },
            { id: 14, title: 'Acil Durum Prosedürleri', units: 5, icon: '🚨' },
            { id: 15, title: 'Yolcu Hizmetleri', units: 4, icon: '🍽️' },
            { id: 16, title: 'Havacılık Terimleri', units: 4, icon: '📚' },
          ].map(module => (
            <Card key={module.id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{module.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{module.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{module.units} ünite</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {type === 'seviye' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">İngilizce Seviye Tespit Sınavı</h3>
                <p className="text-gray-500">30 soru • A1-C1 seviye belirleme • 30 dakika</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-2xl font-bold text-blue-600">30</p>
                <p className="text-sm text-gray-600">Soru</p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl">
                <p className="text-2xl font-bold text-green-600">5</p>
                <p className="text-sm text-gray-600">Seviye (A1-C1)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl">
                <p className="text-2xl font-bold text-purple-600">30</p>
                <p className="text-sm text-gray-600">Dakika</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Liderlik Tablosu İçeriği - Modüler Component Kullanılıyor
  const LeaderboardContentWrapper = () => (
    <LeaderboardContent students={students} />
  );


  // Raporlar İçeriği
  // Eğitim Danışmanları İçeriği
  // Ayarlar İçeriği
  const SettingsContent = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Sistem Ayarları</h2>
        <p className="text-gray-500">Platform yapılandırması ve tercihler</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Genel Ayarlar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">Yeni Kayıtları Kabul Et</p>
                <p className="text-sm text-gray-500">Öğrenci kayıt formunu aktif tut</p>
              </div>
              <div className="w-12 h-6 bg-green-500 rounded-full relative cursor-pointer">
                <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 shadow" />
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">E-posta Bildirimleri</p>
                <p className="text-sm text-gray-500">Yeni kayıtlarda bildirim al</p>
              </div>
              <div className="w-12 h-6 bg-green-500 rounded-full relative cursor-pointer">
                <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 shadow" />
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">Bakım Modu</p>
                <p className="text-sm text-gray-500">Platformu geçici olarak kapat</p>
              </div>
              <div className="w-12 h-6 bg-gray-300 rounded-full relative cursor-pointer">
                <div className="w-5 h-5 bg-white rounded-full absolute left-0.5 top-0.5 shadow" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-500" />
              Veri Yönetimi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full justify-start">
              <Database className="w-4 h-4 mr-2" />
              Veritabanını Yedekle
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <FileText className="w-4 h-4 mr-2" />
              Tüm Verileri Dışa Aktar
            </Button>
            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700">
              <X className="w-4 h-4 mr-2" />
              Önbelleği Temizle
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-amber-500" />
            Sistem Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500">Platform Versiyonu</p>
              <p className="font-bold text-gray-800">v2.0.0</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500">Son Güncelleme</p>
              <p className="font-bold text-gray-800">{new Date().toLocaleDateString('tr-TR')}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500">Toplam Kullanıcı</p>
              <p className="font-bold text-gray-800">{students.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Student Modal
  const StudentModal = () => {
    if (!showStudentModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 rounded-t-2xl">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                  {selectedStudent?.name?.charAt(0)}
                </div>
                <div>
                  <h3 className="font-bold">{selectedStudent?.name}</h3>
                  <p className="text-sm text-white/80">{selectedStudent?.email}</p>
                </div>
              </div>
              <button onClick={() => setShowStudentModal(false)} className="p-1 hover:bg-white/20 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-5">
            {detailLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-blue-50 p-3 rounded-xl text-center">
                    <p className="text-xl font-bold text-blue-600">{studentDetail?.progress?.miles || 0}</p>
                    <p className="text-xs text-gray-500">Mil</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-xl text-center">
                    <p className="text-xl font-bold text-orange-600">{studentDetail?.progress?.streak || 0}</p>
                    <p className="text-xs text-gray-500">Seri</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-xl text-center">
                    <p className="text-xl font-bold text-green-600">{studentDetail?.progress?.completed_lessons?.length || 0}</p>
                    <p className="text-xs text-gray-500">Ders</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl text-center">
                    <p className="text-xl font-bold text-purple-600">{studentDetail?.student?.current_level || 'A0'}</p>
                    <p className="text-xs text-gray-500">Seviye</p>
                  </div>
                </div>
                {/* Kayıt Tarihi */}
                <div className="bg-gray-50 p-3 rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">📅 Kayıt Tarihi</span>
                    <span className="text-sm font-medium text-gray-800">
                      {selectedStudent?.created_at && selectedStudent?.created_at !== '-'
                        ? new Date(selectedStudent.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                        : '-'}
                    </span>
                  </div>
                </div>
                
                {/* Sözleşme ve Giriş Durumu */}
                <div className="bg-gray-50 p-3 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">📄 Sözleşme</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${selectedStudent?.agreements_accepted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {selectedStudent?.agreements_accepted ? '✓ Kabul Edildi' : '✗ Kabul Edilmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">🔐 Son Giriş</span>
                    <span className="text-sm font-medium text-gray-800">
                      {selectedStudent?.last_login 
                        ? new Date(selectedStudent.last_login).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : 'Hiç girmedi'}
                    </span>
                  </div>
                </div>

                {/* Eğitim Ücreti Bilgisi (Sadece Görüntüleme) */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">💰 Eğitim Ücreti</span>
                    {selectedStudent?.is_frozen && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">❄️ Kayıt Dondurulmuş</span>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800">{(selectedStudent?.total_fee || 0).toLocaleString('tr-TR')} ₺</p>
                    <p className="text-xs text-gray-500">Satış Tutarı</p>
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Ödeme işlemleri için Muhasebe bölümünü kullanın
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    size="sm"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    🔑 Şifre Değiştir
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100" 
                    size="sm"
                    onClick={() => {
                      setShowStudentModal(false);
                      setActiveSection('accounting-payments');
                    }}
                  >
                    💳 Muhasebe'ye Git
                  </Button>
                </div>
                <div className="flex gap-2">
                  {!selectedStudent?.is_frozen ? (
                    <Button 
                      variant="outline" 
                      className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50" 
                      size="sm"
                      onClick={async () => {
                        if (window.confirm('Bu öğrencinin kaydını dondurmak istediğinize emin misiniz?')) {
                          try {
                            await axios.post(`${API}/admin/student/${selectedStudent.id}/freeze`, 
                              { is_frozen: true, reason: 'Ödeme yapılmadı' },
                              { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }}
                            );
                            toast.success('Kayıt donduruldu');
                            setSelectedStudent({ ...selectedStudent, is_frozen: true });
                            loadData();
                          } catch (err) {
                            toast.error('İşlem başarısız');
                          }
                        }
                      }}
                    >
                      ❄️ Kaydı Dondur
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="flex-1 text-green-600 border-green-200 hover:bg-green-50" 
                      size="sm"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/student/${selectedStudent.id}/freeze`, 
                            { is_frozen: false },
                            { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }}
                          );
                          toast.success('Kayıt açıldı');
                          setSelectedStudent({ ...selectedStudent, is_frozen: false });
                          loadData();
                        } catch (err) {
                          toast.error('İşlem başarısız');
                        }
                      }}
                    >
                      ✅ Kaydı Aç
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Sidebar />
      
      {/* Main Content */}
      <div className={cn("transition-all duration-300", sidebarOpen ? "ml-64" : "ml-20")}>
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-800">
                  {activeSection === 'dashboard' && 'Dashboard'}
                  {activeSection === 'student-hub' && '👥 Öğrenci İşlemleri'}
                  {activeSection === 'student-list' && '📋 Öğrenci Listesi'}
                  {activeSection === 'student-register' && '➕ Yeni Öğrenci Kayıt'}
                  {activeSection === 'registration-requests' && '🔔 Kayıt Talepleri'}
                  {activeSection === 'student-accounting' && '💰 Öğrenci Muhasebe'}
                  {activeSection === 'student-reports' && '📊 Öğrenci Raporları'}
                  {activeSection === 'student-search' && '🔍 Öğrenci Arama'}
                  {activeSection === 'students' && 'Öğrenciler'}
                  {activeSection === 'reports-hub' && '📊 Raporlar'}
                  {activeSection === 'statistics' && '📊 İstatistikler'}
                  {activeSection === 'reports' && '📄 Öğrenci Raporları'}
                  {activeSection === 'leaderboard' && '🏆 Liderlik Tablosu'}
                  {activeSection === 'scoring' && '🎯 Puanlama Yönetimi'}
                  {activeSection === 'documents' && '📚 Dokümanlar'}
{activeSection === 'placement-results' && '📋 Seviye Sınavı Sonuçları'}
                  {activeSection === 'online' && '🟢 Online İzleme'}
                  {activeSection === 'podcasts' && '🎙️ Podcast Yönetimi'}
                  {activeSection === 'messages' && '💬 Mesajlar & Bildirimler'}
                  {activeSection === 'settings' && 'Ayarlar'}
                  {activeSection === 'egitimler-hub' && '📚 Eğitimler'}
                  {activeSection === 'egitim-ingilizce' && '📚 İngilizce Eğitimi'}
                  {activeSection === 'egitim-kabin' && '✈️ Kabin Memurluğu'}
                  {activeSection === 'egitim-seviye' && '🎯 Seviye Testi'}
                  {activeSection === 'skill-tests' && '🎤 Beceri Testleri'}
                  {activeSection === 'video-courses' && '📹 Videolu Dersler'}
                  {activeSection === 'student-management' && '📚 Öğrenci Ders Durumu'}
                  {activeSection === 'counselors' && '👨‍🏫 Eğitim Danışmanları'}
                  {activeSection === 'gamification-spin' && '🎰 Spin Çarkı Yönetimi'}
                  {activeSection === 'gamification-levels' && '🎖️ Kariyer Seviyeleri Yönetimi'}
                  {activeSection === 'gamification-tasks' && '📋 Günlük Görevler Yönetimi'}
                  {activeSection === 'gamification-tickets' && '✈️ Uçuş Rotaları Yönetimi'}
                  {activeSection === 'gamification-stats' && '📊 Oyuncu İstatistikleri'}
                  {activeSection === 'sertifikalar-hub' && '🏆 Sertifikalar'}
                  {activeSection === 'education-programs' && '📚 Eğitim Programları'}
                  {activeSection === 'bulk-certificates' && '📋 Toplu Sertifika Basımı'}
                  {activeSection === 'certificates' && '🏆 Sertifika Yönetimi'}
                  {activeSection === 'certificate-design' && '🎨 Sertifika Tasarımı'}
                  {activeSection === 'agreements' && '📄 Sözleşme Yönetimi'}
                  {activeSection === 'muhasebe-hub' && '💰 Muhasebe'}
                  {activeSection === 'accounting-payments' && '💳 Ödeme Yönetimi'}
                  {activeSection === 'accounting-invoices' && '📄 Fatura Yönetimi'}
                  {activeSection === 'accounting-reports' && '📊 Mali Raporlar'}
                </h1>
              </div>
            </div>
            
            {/* Eğitim Sayfası Linki - Orta */}
            <a 
              href="/dashboard" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
            >
              <GraduationCap className="w-5 h-5" />
              <span className="font-medium">Eğitim Sayfası</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={loadData}>
                <RefreshCcw className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Bell className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {activeSection === 'dashboard' && (
            <DashboardContent 
              user={user}
              students={students}
              syncLeaderboardData={syncLeaderboardData}
              setActiveSection={setActiveSection}
              openStudentModal={(student) => {
                setSelectedStudent(student);
                setShowStudentModal(true);
              }}
            />
          )}
          {activeSection === 'student-hub' && (
            <StudentHubContent 
              students={students}
              registrationRequests={registrationRequests}
              counselors={counselors}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredStudents={filteredStudents}
              setActiveSection={setActiveSection}
              setSelectedStudent={setSelectedStudent}
              setShowStudentModal={setShowStudentModal}
            />
          )}
          {activeSection === 'student-list' && <StudentsContentWrapper />}
          {activeSection === 'student-register' && <StudentRegisterContent />}
          {activeSection === 'registration-requests' && (
            <RegistrationRequestsContent 
              registrationRequests={registrationRequests}
              approveRegistration={approveRegistration}
              rejectRegistration={rejectRegistration}
              setActiveSection={setActiveSection}
              loadData={loadData}
            />
          )}
          {activeSection === 'student-accounting' && <StudentAccountingContent />}
          {activeSection === 'student-reports' && (
            <StudentReportsContent 
              students={students}
              counselors={counselors}
              setActiveSection={setActiveSection}
              setSelectedStudent={setSelectedStudent}
              setShowStudentModal={setShowStudentModal}
            />
          )}
          {activeSection === 'student-search' && (
            <StudentSearchContent 
              students={students}
              setActiveSection={setActiveSection}
              setSelectedStudent={setSelectedStudent}
              setShowStudentModal={setShowStudentModal}
            />
          )}
          {activeSection === 'students' && <StudentsContentWrapper />}
          
          {/* Eğitimler Hub ve Alt Sayfaları */}
          {activeSection === 'egitimler-hub' && <EgitimlerHubContent setActiveSection={setActiveSection} />}
          {activeSection === 'egitim-ingilizce' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📚 İngilizce Eğitimi</h2>
              </div>
              <ContentManagement educationType="english" />
            </div>
          )}
          {activeSection === 'egitim-kabin' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">✈️ Kabin Memurluğu</h2>
              </div>
              <ContentManagement educationType="cabin_crew" />
            </div>
          )}
          {activeSection === 'egitim-seviye' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎯 Seviye Testi</h2>
              </div>
              <LevelTestManagement />
            </div>
          )}
          {activeSection === 'skill-tests' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎤 Beceri Testleri</h2>
              </div>
              <SkillTestManagement />
            </div>
          )}
          {activeSection === 'video-courses' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📹 Videolu Dersler</h2>
              </div>
              <VideoCoursesManagement />
            </div>
          )}
          
          {activeSection === 'student-management' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              </div>
              <StudentManagement />
            </div>
          )}
          {activeSection === 'reports-hub' && <ReportsHubContent setActiveSection={setActiveSection} />}
          {activeSection === 'scoring' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎯 Puanlama Yönetimi</h2>
              </div>
              <ScoringManagement />
            </div>
          )}
          {activeSection === 'leaderboard' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🏆 Liderlik Tablosu</h2>
              </div>
              <LeaderboardContentWrapper />
            </div>
          )}
          {activeSection === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📚 Dokümanlar</h2>
              </div>
              
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-7 h-7 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Platform Dokümanları</h3>
                    <p className="text-gray-600">Kullanıcılara gönderilecek kılavuz ve içerik belgeleri</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Kullanım Kılavuzu */}
                <Card className="border-2 border-cyan-200 hover:border-cyan-400 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center text-white text-2xl">
                        📖
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">Kullanım Kılavuzu</h3>
                        <p className="text-sm text-gray-500">Platform kullanım rehberi</p>
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-2 mb-4">
                      <li>• Kayıt ve giriş işlemleri</li>
                      <li>• Eğitim programları kullanımı</li>
                      <li>• Test ve sınav yönergeleri</li>
                      <li>• AI Mülakat Koçu kullanımı</li>
                      <li>• Gamifikasyon sistemi</li>
                      <li>• Sıkça sorulan sorular</li>
                    </ul>
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                        onClick={() => window.open('/kullanim-kilavuzu.html', '_blank')}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Görüntüle
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                        onClick={() => {
                          const printWindow = window.open('/kullanim-kilavuzu.html', '_blank');
                          printWindow.onload = () => setTimeout(() => printWindow.print(), 500);
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF İndir
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Eğitim İçerik Özeti */}
                <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center text-white text-2xl">
                        📚
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">Eğitim İçerik Özeti</h3>
                        <p className="text-sm text-gray-500">Tüm müfredat ve ders içerikleri</p>
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-2 mb-4">
                      <li>• 9 seviyeli İngilizce müfredatı</li>
                      <li>• 16 modüllük kabin eğitimi</li>
                      <li>• Ünite ve ders detayları</li>
                      <li>• Havacılık terimleri sözlüğü</li>
                      <li>• Örnek anonslar</li>
                    </ul>
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => window.open('/egitim-icerik.html', '_blank')}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Görüntüle
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => {
                          const printWindow = window.open('/egitim-icerik.html', '_blank');
                          printWindow.onload = () => setTimeout(() => printWindow.print(), 500);
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF İndir
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tüm Dokümanlar Sayfası */}
              <Card className="border border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Globe className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">Dokümanlar Sayfası</h3>
                        <p className="text-sm text-gray-500">Kullanıcıların erişebileceği doküman sayfası</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => window.open('/dokumanlar.html', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Sayfayı Aç
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Bilgi Kutusu */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600">💡</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">PDF Olarak Kaydetme</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      "PDF İndir" butonuna tıkladığınızda açılan pencerede yazıcı olarak "PDF olarak kaydet" seçeneğini seçin.
                      Bu şekilde dokümanları PDF formatında bilgisayarınıza kaydedebilirsiniz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeSection === 'online' && <AdminOnline onSelectStudent={(id) => setProfileViewStudentId(id)} />}
          {activeSection === 'placement-results' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📋 Seviye Tespit Sınavı Sonuçları</h2>
                <span className="text-sm text-gray-500">({placementTotal} sonuç)</span>
              </div>
              
              {/* Seviye Dağılımı Özet */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {levelDistributionData.map((level) => (
                  <Card key={level.name} className="border-l-4" style={{borderLeftColor: level.color}}>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold" style={{color: level.color}}>{level.value}</p>
                      <p className="text-sm text-gray-500">{level.name} Seviye</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Sonuç Tablosu */}
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4 font-medium">Öğrenci</th>
                        <th className="text-left p-4 font-medium">E-posta</th>
                        <th className="text-left p-4 font-medium">Telefon</th>
                        <th className="text-center p-4 font-medium">Seviye</th>
                        <th className="text-center p-4 font-medium">Puan</th>
                        <th className="text-left p-4 font-medium">Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {placementResults.map((result, idx) => (
                        <tr key={idx} className="border-t hover:bg-gray-50">
                          <td className="p-4 font-medium">{result.user_name}</td>
                          <td className="p-4 text-gray-600">{result.user_email}</td>
                          <td className="p-4 text-gray-600">{result.user_phone || '-'}</td>
                          <td className="p-4 text-center">
                            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                              {result.determined_level || 'Belirsiz'}
                            </span>
                          </td>
                          <td className="p-4 text-center font-medium">{result.score || 0}</td>
                          <td className="p-4 text-gray-500 text-sm">
                            {result.completed_at ? new Date(result.completed_at).toLocaleDateString('tr-TR') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {placementResults.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      Henüz seviye sınavı sonucu yok
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          {activeSection === "podcasts" && <PodcastManagement />}
          {activeSection === 'messages' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">💬 Mesajlar & Bildirimler</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Öğrenci Mesajları */}
                <div 
                  onClick={() => setActiveSection('student-messages')}
                  className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-blue-500"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">Öğrenci Mesajları</h3>
                      <p className="text-sm text-gray-500">Gelen kutusunu yönet</p>
                    </div>
                  </div>
                </div>

                {/* SMS Hatırlatma */}
                <div 
                  onClick={() => setActiveSection('sms-reminder')}
                  className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-green-500"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">📱</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">SMS Hatırlatma</h3>
                      <p className="text-sm text-gray-500">İnaktif öğrenci bildirimleri</p>
                    </div>
                  </div>
                </div>

                {/* E-posta Bildirimleri */}
                <div 
                  onClick={() => setActiveSection('email-reminder')}
                  className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-purple-500"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">📧</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">E-posta Bildirimleri</h3>
                      <p className="text-sm text-gray-500">İnaktif öğrenci e-postaları</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeSection === 'student-messages' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('messages')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">💬 Öğrenci Mesajları</h2>
              </div>
              <AdminMessages />
            </div>
          )}
          {activeSection === 'sms-reminder' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('messages')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              </div>
              <SmsReminderManagement />
            </div>
          )}
          {activeSection === 'email-reminder' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('messages')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              </div>
              <EmailReminderManagement />
            </div>
          )}
          {activeSection === 'reports' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📄 Öğrenci Raporları</h2>
              </div>
              <ReportsContent students={students} />
            </div>
          )}
          {activeSection === 'statistics' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('reports-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📊 İstatistikler</h2>
              </div>
              <StatisticsContent monthlyProgressData={monthlyProgressData} />
            </div>
          )}
          {activeSection === 'counselors' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              </div>
              <CounselorsContent 
                counselors={counselors}
                setEditingCounselor={setEditingCounselor}
                setNewCounselor={setNewCounselor}
                setShowCounselorModal={setShowCounselorModal}
                openEditCounselor={openEditCounselor}
                handleDeleteCounselor={handleDeleteCounselor}
              />
            </div>
          )}
          {activeSection === 'settings' && <SettingsContent />}
          
          {/* İş İlanları Yönetimi */}
          {activeSection === 'jobs-management' && <JobsManagement />}
          
          {/* Video Mülakat Yönetimi */}
          {activeSection === 'video-interview' && <VideoInterviewManagement />}
          
          {/* Mülakat Soruları Yönetimi */}
          {activeSection === 'interview-hub' && <InterviewHubManagement />}
          
          {/* Psikoloji Testleri Yönetimi */}
          {activeSection === 'psychology-tests' && <PsychologyTestsManagement />}
          
          {/* Gamification Sayfaları */}
          {activeSection === 'gamification-spin' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎰 Spin Çarkı</h2>
              </div>
              <SpinWheelManagement />
            </div>
          )}
          {activeSection === 'gamification-levels' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎖️ Kariyer Seviyeleri</h2>
              </div>
              <CareerLevelsManagement />
            </div>
          )}
          {activeSection === 'gamification-tasks' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📋 Günlük Görevler</h2>
              </div>
              <DailyTasksManagement students={students} />
            </div>
          )}
          {activeSection === 'gamification-tickets' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">✈️ Uçuş Rotaları</h2>
              </div>
              <FlightTicketsManagement />
            </div>
          )}
          {activeSection === 'gamification-stats' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('egitimler-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📊 Oyuncu İstatistikleri</h2>
              </div>
              <GamificationStatsContent students={students} />
            </div>
          )}
          
          {/* Sertifikalar Hub ve Alt Sayfaları */}
          {activeSection === 'sertifikalar-hub' && <SertifikalarHubContent setActiveSection={setActiveSection} />}
          {activeSection === 'education-programs' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('sertifikalar-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📚 Eğitim Programları</h2>
              </div>
              <EducationProgramsManagement />
            </div>
          )}
          {activeSection === 'bulk-certificates' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('sertifikalar-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📋 Toplu Sertifika Basımı</h2>
              </div>
              <BulkCertificatesManagement />
            </div>
          )}
          {activeSection === 'certificates' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('sertifikalar-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🏆 Sertifika Yönetimi</h2>
              </div>
              <CertificateManagement students={students} />
            </div>
          )}
          {activeSection === 'certificate-design' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('sertifikalar-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">🎨 Sertifika Tasarımı</h2>
              </div>
              <CertificateDesigner />
            </div>
          )}
          {activeSection === 'agreements' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('sertifikalar-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📄 Sözleşme Yönetimi</h2>
              </div>
              <AgreementEditor />
            </div>
          )}
          
          {/* Muhasebe Hub ve Alt Sayfaları */}
          {activeSection === 'muhasebe-hub' && <MuhasebeHubContent students={students} setActiveSection={setActiveSection} />}
          {activeSection === 'accounting-payments' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('muhasebe-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">💳 Ödeme Yönetimi</h2>
              </div>
              <PaymentManagement students={students} />
            </div>
          )}
          {activeSection === 'accounting-invoices' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('muhasebe-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📄 Fatura Yönetimi</h2>
              </div>
              <Card>
                <CardContent className="p-6">
                  <p className="text-gray-600">Fatura yönetimi modülü yakında eklenecek...</p>
                </CardContent>
              </Card>
            </div>
          )}
          {activeSection === 'accounting-reports' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => setActiveSection('muhasebe-hub')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
                <h2 className="text-2xl font-bold text-gray-800">📊 Mali Raporlar</h2>
              </div>
              <Card>
                <CardContent className="p-6">
                  <p className="text-gray-600">Mali raporlar modülü yakında eklenecek...</p>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      <StudentModal />
      
      {/* Danışman Modal */}
      {showCounselorModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold">{editingCounselor ? 'Danışman Düzenle' : 'Yeni Danışman'}</h3>
                </div>
                <button 
                  onClick={() => { setShowCounselorModal(false); setEditingCounselor(null); }} 
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                <Input
                  placeholder="Danışman adı soyadı"
                  value={newCounselor.name}
                  onChange={(e) => setNewCounselor({ ...newCounselor, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ünvan</label>
                <Input
                  placeholder="Kıdemli Eğitim Danışmanı"
                  value={newCounselor.title}
                  onChange={(e) => setNewCounselor({ ...newCounselor, title: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                  <Input
                    type="email"
                    placeholder="ornek@email.com"
                    value={newCounselor.email}
                    onChange={(e) => setNewCounselor({ ...newCounselor, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <Input
                    placeholder="05XX XXX XX XX"
                    value={newCounselor.phone}
                    onChange={(e) => setNewCounselor({ ...newCounselor, phone: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Uzmanlık Alanı</label>
                <Input
                  placeholder="İngilizce Eğitimi, Kabin Memurluğu"
                  value={newCounselor.specialization}
                  onChange={(e) => setNewCounselor({ ...newCounselor, specialization: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={newCounselor.is_active}
                  onChange={(e) => setNewCounselor({ ...newCounselor, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Aktif Danışman</label>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowCounselorModal(false); setEditingCounselor(null); }}
                >
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleAddCounselor}
                >
                  {editingCounselor ? 'Güncelle' : 'Danışman Ekle'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Yeni Öğrenci Ekleme / Düzenleme Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl my-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold">{editMode ? 'Öğrenci Düzenle' : 'Yeni Öğrenci Ekle'}</h3>
                </div>
                <button 
                  onClick={() => { setShowAddStudentModal(false); setEditMode(false); }} 
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Kişisel Bilgiler */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Kişisel Bilgiler
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                    <Input
                      placeholder="Öğrencinin adı soyadı"
                      value={newStudent.name}
                      onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TC Kimlik No</label>
                    <Input
                      placeholder="11 haneli TC kimlik"
                      maxLength={11}
                      value={newStudent.tc_no}
                      onChange={(e) => setNewStudent({ ...newStudent, tc_no: e.target.value.replace(/\D/g, '') })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Doğum Tarihi</label>
                    <Input
                      type="date"
                      value={newStudent.birth_date}
                      onChange={(e) => setNewStudent({ ...newStudent, birth_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cinsiyet</label>
                    <select 
                      className="w-full h-10 px-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newStudent.gender}
                      onChange={(e) => setNewStudent({ ...newStudent, gender: e.target.value })}
                    >
                      <option value="">Seçiniz</option>
                      <option value="Erkek">Erkek</option>
                      <option value="Kadın">Kadın</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* İletişim Bilgileri */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> İletişim Bilgileri
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-posta *</label>
                    <Input
                      type="email"
                      placeholder="ornek@email.com"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      disabled={editMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cep Telefonu</label>
                    <Input
                      placeholder="05XX XXX XX XX"
                      value={newStudent.phone}
                      onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Şehir</label>
                    <Input
                      placeholder="İstanbul"
                      value={newStudent.city}
                      onChange={(e) => setNewStudent({ ...newStudent, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eğitim Durumu</label>
                    <select 
                      className="w-full h-10 px-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newStudent.education_level}
                      onChange={(e) => setNewStudent({ ...newStudent, education_level: e.target.value })}
                    >
                      <option value="">Seçiniz</option>
                      <option value="İlkokul">İlkokul</option>
                      <option value="Ortaokul">Ortaokul</option>
                      <option value="Lise">Lise</option>
                      <option value="Ön Lisans">Ön Lisans</option>
                      <option value="Lisans">Lisans</option>
                      <option value="Yüksek Lisans">Yüksek Lisans</option>
                      <option value="Doktora">Doktora</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={2}
                      placeholder="Açık adres"
                      value={newStudent.address}
                      onChange={(e) => setNewStudent({ ...newStudent, address: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Şifre (Sadece yeni kayıt) */}
              {!editMode && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Giriş Bilgileri
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Şifre *</label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="En az 6 karakter"
                          value={newStudent.password}
                          onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })}
                          minLength={6}
                          required
                          data-testid="new-student-password-input"
                          className={newStudent.password && newStudent.password.length < 6 ? "border-red-500" : ""}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                            let password = '';
                            for (let i = 0; i < 8; i++) {
                              password += chars.charAt(Math.floor(Math.random() * chars.length));
                            }
                            setNewStudent({ ...newStudent, password });
                            toast.success('Şifre oluşturuldu: ' + password);
                          }}
                          data-testid="generate-password-btn"
                          className="whitespace-nowrap"
                        >
                          <RefreshCcw className="w-4 h-4 mr-1" />
                          Oluştur
                        </Button>
                      </div>
                      {newStudent.password && newStudent.password.length < 6 && (
                        <p className="text-red-500 text-xs mt-1">Şifre en az 6 karakter olmalıdır</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Şifre Sıfırlama (Düzenleme modunda) */}
              {editMode && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Şifre Yönetimi
                  </h4>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-gray-700">Şifre Sıfırla</p>
                        <p className="text-sm text-gray-500">Öğrenciye yeni şifre tanımla</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Yeni şifre (en az 6 karakter)"
                        value={newStudent.password || ''}
                        onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })}
                        className={newStudent.password && newStudent.password.length < 6 ? "border-red-500" : ""}
                        data-testid="edit-student-password-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                          let password = '';
                          for (let i = 0; i < 8; i++) {
                            password += chars.charAt(Math.floor(Math.random() * chars.length));
                          }
                          setNewStudent({ ...newStudent, password });
                          toast.success('Şifre oluşturuldu: ' + password);
                        }}
                        className="whitespace-nowrap"
                        data-testid="edit-generate-password-btn"
                      >
                        <RefreshCcw className="w-4 h-4 mr-1" />
                        Oluştur
                      </Button>
                    </div>
                    {newStudent.password && newStudent.password.length < 6 && (
                      <p className="text-red-500 text-xs mt-2">Şifre en az 6 karakter olmalıdır</p>
                    )}
                    {newStudent.password && newStudent.password.length >= 6 && (
                      <p className="text-green-600 text-xs mt-2">✓ Kaydet'e tıkladığınızda şifre güncellenecek</p>
                    )}
                  </div>
                </div>
              )}

              {/* Eğitim Ataması */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> Eğitim Ataması
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div 
                    onClick={() => toggleCourse('ingilizce')}
                    className={cn(
                      "p-4 rounded-xl border-2 cursor-pointer transition-all",
                      newStudent.assigned_courses?.includes('ingilizce') 
                        ? "border-blue-500 bg-blue-50" 
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center text-xl",
                        newStudent.assigned_courses?.includes('ingilizce') ? "bg-blue-500 text-white" : "bg-gray-100"
                      )}>
                        🌍
                      </div>
                      <div>
                        <p className="font-semibold">İngilizce Eğitimi</p>
                        <p className="text-sm text-gray-500">A0-C1 seviye eğitim</p>
                      </div>
                      {newStudent.assigned_courses?.includes('ingilizce') && (
                        <CheckCircle className="w-5 h-5 text-blue-500 ml-auto" />
                      )}
                    </div>
                  </div>
                  <div 
                    onClick={() => toggleCourse('kabin')}
                    className={cn(
                      "p-4 rounded-xl border-2 cursor-pointer transition-all",
                      newStudent.assigned_courses?.includes('kabin') 
                        ? "border-sky-500 bg-sky-50" 
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center text-xl",
                        newStudent.assigned_courses?.includes('kabin') ? "bg-sky-500 text-white" : "bg-gray-100"
                      )}>
                        ✈️
                      </div>
                      <div>
                        <p className="font-semibold">Kabin Memurluğu</p>
                        <p className="text-sm text-gray-500">16 modül eğitim</p>
                      </div>
                      {newStudent.assigned_courses?.includes('kabin') && (
                        <CheckCircle className="w-5 h-5 text-sky-500 ml-auto" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Eğitim Danışmanı */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" /> Eğitim Danışmanı
                </h4>
                <select 
                  className="w-full h-10 px-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={newStudent.counselor_id}
                  onChange={(e) => setNewStudent({ ...newStudent, counselor_id: e.target.value })}
                >
                  <option value="">Danışman Seçiniz</option>
                  {counselors.filter(c => c.is_active !== false).map(counselor => (
                    <option key={counselor.id} value={counselor.id}>
                      {counselor.name} {counselor.title ? `(${counselor.title})` : ''} - {counselor.student_count || 0} öğrenci
                    </option>
                  ))}
                </select>
                {counselors.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    ⚠️ Henüz danışman eklenmemiş. Önce &quot;Eğitim Danışmanları&quot; bölümünden danışman ekleyin.
                  </p>
                )}
              </div>

              {/* Eğitim Ücreti */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Wallet className="w-4 h-4" /> Eğitim Ücreti
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Satış Tutarı (₺) *</label>
                    <Input
                      type="number"
                      placeholder="Örn: 15000"
                      value={newStudent.total_fee}
                      onChange={(e) => setNewStudent({ ...newStudent, total_fee: parseFloat(e.target.value) || 0 })}
                      data-testid="student-total-fee"
                    />
                    <p className="text-xs text-gray-500 mt-1">Öğrencinin toplam eğitim ücreti</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bilgi</label>
                    <div className="h-10 px-3 flex items-center rounded-md bg-gray-50 text-gray-600 text-sm">
                      Ödeme işlemleri Muhasebe bölümünden yapılır
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowAddStudentModal(false); setEditMode(false); }}
                >
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={editMode ? handleUpdateStudent : handleAddStudent}
                  disabled={addingStudent}
                >
                  {addingStudent ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Öğrenci Ekle')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import Modal */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold">Excelden Öğrenci Aktar</h3>
                    <p className="text-sm text-white/80">CRM listesinden toplu aktarım</p>
                  </div>
                </div>
                <button 
                  onClick={() => { 
                    setShowExcelModal(false); 
                    setExcelFile(null); 
                    setImportResult(null); 
                  }} 
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Desteklenen Sütunlar Bilgisi */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" /> Desteklenen Excel Sütunları
                </h4>
                <div className="text-sm text-blue-700 grid grid-cols-2 gap-1">
                  <span>• AdSoyad</span>
                  <span>• EMail</span>
                  <span>• CepTelefon</span>
                  <span>• TcNo</span>
                  <span>• DogumTarihi</span>
                  <span>• Cinsiyet</span>
                  <span>• SehirAdi</span>
                  <span>• Adres</span>
                  <span>• EgitimDurumuAdi</span>
                  <span>• KayTar</span>
                  <span>• EgitimDanismanAdSoyad</span>
                  <span>• MuhasebeBorc</span>
                  <span>• MuhasebeOdenen</span>
                  <span>• DersAdi</span>
                  <span>• Sifre</span>
                </div>
              </div>
              
              {/* Dosya Yükleme Alanı */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-green-500 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setExcelFile(e.target.files[0])}
                  className="hidden"
                  id="excel-file-input"
                />
                <label 
                  htmlFor="excel-file-input" 
                  className="cursor-pointer"
                >
                  {excelFile ? (
                    <div className="space-y-2">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                      <p className="font-medium text-gray-800">{excelFile.name}</p>
                      <p className="text-sm text-gray-500">Dosya seçildi - Yüklemek için butona tıklayın</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="font-medium text-gray-800">Excel Dosyası Seçin</p>
                      <p className="text-sm text-gray-500">.xlsx veya .xls formatında</p>
                    </div>
                  )}
                </label>
              </div>
              
              {/* İmport Sonucu */}
              {importResult && (
                <div className={cn(
                  "rounded-xl p-4",
                  importResult.error ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"
                )}>
                  {importResult.error ? (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Hata Oluştu</p>
                        <p className="text-sm text-red-600">{importResult.error}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">{importResult.message}</span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-700">✓ Aktarılan: {importResult.imported}</span>
                        <span className="text-yellow-700">⊘ Atlanan: {importResult.skipped}</span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-xs font-medium text-gray-600 mb-1">Hatalar:</p>
                          {importResult.errors.map((err, idx) => (
                            <p key={idx} className="text-xs text-red-600">{err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { 
                    setShowExcelModal(false); 
                    setExcelFile(null); 
                    setImportResult(null); 
                  }}
                >
                  Kapat
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleExcelImport}
                  disabled={!excelFile || importing}
                >
                  {importing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Aktarılıyor...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Öğrencileri Aktar
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Profile Viewer Modal */}
      {profileViewStudentId && (
        <StudentProfileViewer
          studentId={profileViewStudentId}
          onClose={() => setProfileViewStudentId(null)}
        />
      )}

      {/* Şifre Değiştirme Modal */}
      {showPasswordModal && selectedStudent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    🔑
                  </div>
                  <div>
                    <h3 className="font-bold">Şifre Değiştir</h3>
                    <p className="text-sm text-white/80">{selectedStudent.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => { 
                    setShowPasswordModal(false); 
                    setNewPassword(''); 
                  }} 
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700">
                  <strong>{selectedStudent.email}</strong> için yeni şifre belirleyin.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre *</label>
                <Input
                  type="password"
                  placeholder="En az 6 karakter"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Şifre en az 6 karakter olmalıdır</p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { 
                    setShowPasswordModal(false); 
                    setNewPassword(''); 
                  }}
                >
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  onClick={handleChangePassword}
                  disabled={!newPassword || newPassword.length < 6 || changingPassword}
                >
                  {changingPassword ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
